import React from 'react';

import type { FullStatsDto, PlayerGraphResultsDto } from '@poker-stats/contracts';

import V2PostflopTable from './V2PostflopTable';
import V2RealChart from './V2RealChart';

import {
  buildPostflopPanel,
  getDefaultSubTab,
  getPostflopViewTabs,
  PREFLOP_NAV,
  type PostflopViewKey,
} from '../utils/postflopBuilders';

type PostflopPanelViewKey = PostflopViewKey | 'Graf';

type V2PostflopPanelProps = {
  stats: Partial<FullStatsDto>;
  graf?: {
    graph?: PlayerGraphResultsDto;
    loading?: boolean;
  };
};

const BASE_VIEWS: PostflopViewKey[] = ['General', 'Preflop', 'Donk', 'CBet', 'B/x'];

const VIEW_SLUG: Record<PostflopPanelViewKey, string> = {
  General: 'general',
  Preflop: 'preflop',
  Donk: 'donk',
  CBet: 'cbet',
  'B/x': 'bx',
  Graf: 'graf',
};

const V2PostflopPanel: React.FC<V2PostflopPanelProps> = ({ stats, graf }) => {
  const views = React.useMemo<PostflopPanelViewKey[]>(
    () => (graf ? [...BASE_VIEWS, 'Graf'] : BASE_VIEWS),
    [graf],
  );
  const [view, setView] = React.useState<PostflopPanelViewKey>('General');
  const [subTab, setSubTab] = React.useState(() => getDefaultSubTab('General'));
  const isGrafView = view === 'Graf';

  React.useEffect(() => {
    if (isGrafView) {
      return;
    }
    setSubTab(getDefaultSubTab(view));
  }, [view, isGrafView]);

  const panel = isGrafView ? null : buildPostflopPanel(stats, view, subTab);
  const sidebarTabs =
    view === 'Preflop'
      ? PREFLOP_NAV.flatMap((group) => group.tabs)
      : getPostflopViewTabs(view as PostflopViewKey);
  const hasTables = panel?.tables.some((entry) => entry.table) ?? false;

  const tableContent = !hasTables ? (
    <p className="table-hint">Нет данных для выбранной группы.</p>
  ) : (
    <div className="postflop-tables">
      {panel?.tables.map((entry, index) =>
        entry.table ? (
          <div className="postflop-table-block" key={entry.title ?? `${view}-${index}`}>
            {entry.title ? <p className="postflop-table-title">{entry.title}</p> : null}
            <V2PostflopTable
              table={entry.table}
              ariaLabel={entry.title ? `${view}: ${entry.title}` : `${view}: таблица`}
              variant="matrix"
            />
          </div>
        ) : null,
      )}
    </div>
  );

  return (
    <section
      className={`stats-panel stats-panel--postflop stats-panel--compact stats-panel--${VIEW_SLUG[view]}`}
      aria-labelledby="postflop-title"
    >
      <div className="postflop-heading">
        <h2 id="postflop-title">Postflop</h2>
        <div className="postflop-tabs postflop-tabs--primary" aria-label="Группа показателей">
          {views.map((label) => (
            <button
              key={label}
              type="button"
              data-postflop={label}
              aria-pressed={view === label}
              onClick={() => setView(label)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {isGrafView ? (
        <div className="postflop-graf-layout">
          <div className="postflop-graf chart-frame chart-frame--portable">
            <V2RealChart graph={graf?.graph} loading={graf?.loading} />
          </div>
        </div>
      ) : (
        <div className="postflop-split-layout">
          <nav className="postflop-split-sidebar" aria-label={`${view}: режим`}>
            {view === 'Preflop'
              ? PREFLOP_NAV.map((group) => (
                  <div
                    key={group.id}
                    className="postflop-split-sidebar-group"
                    aria-label={group.label}
                  >
                    {group.tabs.map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        aria-pressed={subTab === tab.id}
                        onClick={() => setSubTab(tab.id)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                ))
              : sidebarTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    aria-pressed={subTab === tab.id}
                    onClick={() => setSubTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
          </nav>
          <div className="postflop-split-main">{tableContent}</div>
        </div>
      )}
    </section>
  );
};

export default V2PostflopPanel;
